import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";

interface MessageContentProps {
    text: string;
    className?: string;
}

const COLLAPSE_LINE_THRESHOLD = 4;
const COLLAPSE_CHAR_THRESHOLD = 280;

function countLines(text: string): number {
    if (text.length === 0) return 0;
    let lines = 1;
    for (let i = 0; i < text.length; i += 1) {
        if (text.charCodeAt(i) === 10) lines += 1;
    }
    return lines;
}

function buildCollapsedPreview(text: string): string {
    const limitByChars =
        text.length > COLLAPSE_CHAR_THRESHOLD ? text.slice(0, COLLAPSE_CHAR_THRESHOLD) : text;
    const lines = limitByChars.split("\n");
    if (lines.length > COLLAPSE_LINE_THRESHOLD) {
        return lines.slice(0, COLLAPSE_LINE_THRESHOLD).join("\n");
    }
    return limitByChars;
}

export function MessageContent({ text, className }: MessageContentProps) {
    const { t } = useTranslation("chat");
    const [expanded, setExpanded] = useState(false);

    const needsCollapse = text.length > COLLAPSE_CHAR_THRESHOLD || countLines(text) > COLLAPSE_LINE_THRESHOLD;
    const display = !needsCollapse || expanded ? text : `${buildCollapsedPreview(text)}…`;

    return (
        <div
            className={cn(
                "text-sm leading-relaxed whitespace-pre-wrap text-zinc-100 md:text-base",
                className,
            )}
        >
            {display}
            {needsCollapse && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-1 block text-xs font-medium text-zinc-400 hover:text-zinc-200"
                >
                    {expanded ? t("messages.showLess") : t("messages.showMore")}
                </button>
            )}
        </div>
    );
}
